import { IconButton, Modal, styled } from '@mui/material';
import { ReactNode } from 'react';
import { useAtomValue } from 'jotai';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-grey.svg';
import { isSmallScreenState } from '../../../../state/UIState';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Left-hand header content (avatar + name + status, or a plain title). */
  header: ReactNode;
  /** Sticky footer content (e.g. unsaved indicator + action buttons). */
  footer: ReactNode;
  children: ReactNode;
  width?: string;
  top?: string;
};

/**
 * Dedicated modal shell for the User add/edit form. Kept separate from the shared `ModalComponent` so
 * the redesigned header (avatar + name + status) and footer (unsaved indicator + actions) do not affect
 * any other modal in the app. Layout only — no form logic lives here.
 */
export const UserModalShell = ({ isOpen, onClose, header, footer, children, width, top }: Props) => {
  const isSmallDevice = useAtomValue(isSmallScreenState);

  return (
    <Modal open={isOpen} aria-labelledby="user-modal-title">
      <Wrapper width={width} top={top} tabIndex={-1} isSmallDevice={isSmallDevice}>
        <TopBar>
          <HeaderSlot>{header}</HeaderSlot>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            size="small"
            sx={{ ml: 2, p: 0, width: 28, height: 28, flexShrink: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </TopBar>
        <Content>{children}</Content>
        <Footer>{footer}</Footer>
      </Wrapper>
    </Modal>
  );
};

const Wrapper = styled('div')<{ width?: string; top?: string; isSmallDevice: boolean }>`
  width: ${({ width, isSmallDevice }) => (isSmallDevice ? '95%' : width || '640px')};
  max-height: 90vh;
  background-color: ${({ theme }) => theme.colors.surfaceBackground.bg1};
  border-radius: 12px;
  position: absolute;
  top: ${({ top }) => top || '48px'};
  left: 50%;
  transform: translate(-50%, 0);
  display: flex;
  flex-direction: column;
  padding: ${({ isSmallDevice }) => (isSmallDevice ? '16px' : '24px')};
  gap: 16px;
`;

const TopBar = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
`;

const HeaderSlot = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const Content = styled('div')`
  color: ${({ theme }) => theme.colors.text.main};
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  padding-right: 4px;
  flex: 1;
  min-height: 0;
`;

const Footer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;
  padding-top: 4px;
`;
